import { Project } from '@/models'
import { IProjectService as ProjectService } from '@/services/IProjectService'
import { defineStore } from 'pinia'
import { useBaseStore } from '.'
import { FeaturesService, FeatureResponse as Feature } from '@itemis-solutions/platform-client-sdk'
import { notNullish } from '@vueuse/core'

interface ProjectState {
  currentProject: Project | undefined
  projects: Project[]
  sortCriteria: string
  filterDate: string
  filterString: string
}

export const useProjectStore = defineStore('projectStore', {
  state: (): ProjectState => ({
    projects: [],
    currentProject: undefined,
    sortCriteria: '',
    filterDate: '',
    filterString: '',
  }),
  getters: {
    getDateSortedProjects(): Project[] {
      return [...this.projects].sort((a: Project, b: Project) => {
        const da = new Date(a.creationDate as string)
        const db = new Date(b.creationDate as string)

        return db.getTime() - da.getTime()
      })
    },

    getProjectById: (state) => (id: number) => state.projects.find((project) => project.id === id),

    knownProjectSlugs: (state) => state.projects.map((project) => project.slug).filter(notNullish),

    isFeatureActive: (state) => (feature: string | undefined) => {
      return feature == undefined || state.currentProject?.features?.find((f) => f.fqn === feature)
    },
  },
  actions: {
    async loadProjectsFromApi(projectService: ProjectService) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()

      try {
        if (projectService !== undefined) {
          const projectResponse = await projectService.getProjects()
          this.setProjects(projectResponse)
        }
      } catch (error) {
        console.error('Error loading projects:', error)
      } finally {
        baseStore.decrementRunningJobs()
      }
    },
    addNewProject(project: Project) {
      this.projects.push(project)
    },
    async removeProject(projectService: ProjectService, project: Project) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()

      try {
        const data = await projectService.deleteProject(project.id)
        const projectIndex = this.projects.findIndex((item) => item.id === data.id)
        if (projectIndex > -1) {
          this.projects.splice(projectIndex, 1)
        }
      } catch (error) {
        console.error('Error removing project:', error)
      } finally {
        baseStore.decrementRunningJobs()
      }
    },
    async putProject(projectService: ProjectService, project: Project) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      try {
        const updatedProject = await projectService.putProject(project)
        this.updateProject(updatedProject)
        this.setCurrentProject(updatedProject)
        return updatedProject
      } catch (error) {
        console.error('Error updating project:', error)
      } finally {
        baseStore.decrementRunningJobs()
      }
    },
    setProjectAsFavorite(item: Project) {
      this.projects.forEach((project) => {
        if (project.name === item.name && project.id === item.id) {
          project.favorite = item.favorite
        }
      })
    },
    async getFeaturesForProject(project: Project): Promise<Feature[]> {
      if (project && project.resourceId) {
        const { data } = await FeaturesService.getAvailableFeaturesWithDetails({
          itemisResourceId: project.resourceId,
        })
        if (this.currentProject) {
          this.currentProject.features = data
        }
        return data
      }
      return []
    },
    setProjects(projects: Project[]) {
      this.projects = projects
    },
    updateProject(updatedProject: Project) {
      const index = this.projects.findIndex((project) => project.id === updatedProject.id)
      if (index !== -1) {
        this.projects.splice(index, 1, updatedProject)
      }
    },
    async setCurrentProject(project: Project | undefined) {
      if (project) {
        if (project.resourceId) {
          this.getFeaturesForProject(project)
        } else {
          project.features = undefined
        }
      }
      this.currentProject = project
    },
    setSortCriteria(sortCriteria: string) {
      this.sortCriteria = sortCriteria
    },
    setFilterDate(date: string) {
      this.filterDate = date
    },
    setFilterString(filter: string) {
      this.filterString = filter
    },
  },
})
